'use client'
import './Dialog.global.scss'

import * as DialogRadix from '@radix-ui/react-dialog'
import * as VisuallyHidden from '@radix-ui/react-visually-hidden'
import { type JSX, type ReactNode, useEffect } from 'react'

import ModalCloseIcon from '@/assets/icons/modal-close.svg'

import S from './Dialog.module.scss'

// OCE-588: refcount-based flag on <html>, so decorative layers in the page
// (e.g. the ARENA word in HeroBlock) can opt out while any dialog is open.
// Nested dialogs are supported — the attribute is cleared only after the
// last open dialog closes.
let openDialogCount = 0

const acquireDialogOpenFlag = () => {
  if (typeof document === 'undefined') return
  openDialogCount += 1
  document.documentElement.dataset.dialogOpen = ''
}

const releaseDialogOpenFlag = () => {
  if (typeof document === 'undefined') return
  openDialogCount = Math.max(0, openDialogCount - 1)
  if (openDialogCount === 0) {
    delete document.documentElement.dataset.dialogOpen
  }
}

export type { ModalHeaderProps } from './ModalHeader'
export { default as ModalHeader } from './ModalHeader'

/**
 * A visible description inside the dialog content; use it together with
 * `describedByChildren` so screen readers hear the text once.
 */
export const DialogDescription = DialogRadix.Description

export type DialogProps = {
  children: ReactNode | JSX.Element
  title?: ReactNode
  /**
   * OCE-604: short description announced by screen readers alongside the
   * title. Always rendered (visually hidden) so Radix Dialog's a11y
   * contract is satisfied. Callers should pass a localized string; if
   * omitted, a generic fallback is used to keep Radix quiet.
   */
  description?: ReactNode
  /**
   * The children render a `DialogDescription`; no hidden description is
   * added, so the visible text is not announced twice.
   */
  describedByChildren?: boolean
  withoutClose?: boolean
  open: boolean
  toggle: (val: boolean) => void
  className?: string
  scrollable?: boolean
}

const Dialog = ({
  children,
  title,
  description,
  describedByChildren = false,
  withoutClose = false,
  open,
  className,
  toggle,
  scrollable = false
}: DialogProps) => {
  useEffect(() => {
    if (!open) return
    acquireDialogOpenFlag()
    return releaseDialogOpenFlag
  }, [open])

  return (
    <DialogRadix.Root open={open} onOpenChange={toggle}>
      <DialogRadix.Portal>
        <DialogRadix.Overlay className="dialogOverlay" />
        <DialogRadix.Content className={`dialogContent ${className || ''}`}>
          {title ? (
            <>
              <div className={S.header}>
                <DialogRadix.Title className="dialogTitle">
                  {title}
                </DialogRadix.Title>
                {!withoutClose && (
                  <DialogRadix.Close asChild>
                    <button className={S.closeButton}>
                      <ModalCloseIcon className={S.closeIcon} />
                    </button>
                  </DialogRadix.Close>
                )}
              </div>
              {describedByChildren ? null : (
                <VisuallyHidden.Root asChild>
                  <DialogRadix.Description>
                    {description ?? 'Arena dialog'}
                  </DialogRadix.Description>
                </VisuallyHidden.Root>
              )}
              {scrollable ? (
                <div className={S.scrollableContent}>{children}</div>
              ) : (
                children
              )}
            </>
          ) : (
            <>
              <VisuallyHidden.Root asChild>
                <DialogRadix.Title className="dialogTitle">
                  Dialog
                </DialogRadix.Title>
              </VisuallyHidden.Root>
              {describedByChildren ? null : (
                <VisuallyHidden.Root asChild>
                  <DialogRadix.Description>
                    {description ?? 'Arena dialog'}
                  </DialogRadix.Description>
                </VisuallyHidden.Root>
              )}
              {scrollable ? (
                <div className={S.scrollableContent}>{children}</div>
              ) : (
                children
              )}
            </>
          )}
        </DialogRadix.Content>
      </DialogRadix.Portal>
    </DialogRadix.Root>
  )
}

export default Dialog
